// Browser auto-translation (Google Translate in Chrome, Microsoft Translator in Edge)
// and some extensions rewrite text nodes in place. React later tries to remove/insert
// nodes it no longer owns and throws:
//   NotFoundError: Failed to execute 'removeChild' on 'Node'
// This only reproduces in profiles where translation/extensions are active — which is
// exactly why incognito and mobile work.
//
// We disable translation on the shell (translate="no") and, as a safety net, make
// removeChild/insertBefore non-fatal so a mutated subtree degrades instead of blanking
// the whole route.

let installed = false;

export function installDomTranslateGuard() {
  if (installed || typeof window === "undefined") return;
  // Diagnostic escape hatch: /rota?nodomguard=1 disables the safety net so the
  // raw NotFoundError surfaces (used to verify the real fix, not the mask).
  if (window.location.search.includes("nodomguard=1")) return;
  installed = true;


  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn("[FutBux] removeChild on foreign node ignored (DOM mutated externally)", {
        route: window.location.pathname,
        child: (child as unknown as Element)?.nodeName,
      });
      if (child.parentNode) (child.parentNode as Node & { removeChild: typeof originalRemoveChild }).removeChild(child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  } as typeof Node.prototype.removeChild;

  Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, ref: Node | null): T {
    if (ref && ref.parentNode !== this) {
      console.warn("[FutBux] insertBefore with foreign reference node (DOM mutated externally)", {
        route: window.location.pathname,
      });
      return originalInsertBefore.call(this, node, null) as T;
    }
    return originalInsertBefore.call(this, node, ref) as T;
  } as typeof Node.prototype.insertBefore;
}

export function isPageBeingTranslated() {
  if (typeof document === "undefined") return false;
  const html = document.documentElement;
  return (
    html.classList.contains("translated-ltr") ||
    html.classList.contains("translated-rtl") ||
    !!document.querySelector("font[_msttexthash]") ||
    !!document.querySelector(".goog-te-banner-frame")
  );
}
